/**
 * Manage focus among Sample ID inputs after an Enter/Tab attempt.
 *
 * Why this exists:
 * - When a user tries to advance (Enter/Tab), we only want to move focus if the
 *   current input validated successfully. If it failed, we must keep/return focus
 *   to the failing input so it can be corrected.
 * - React/MUI re-renders can occasionally steal focus. To combat this, we re-apply
 *   focus immediately and again in requestAnimationFrame if needed.
 *
 * How it works:
 * - Collect all visible MUI TextField input elements and filter to those that have
 *   the custom 'valid' attribute that we inject via ValidatedTextfield.inputProps.
 *   This distinguishes Sample ID fields from other inputs on the page.
 * - If any Sample ID inputs are currently invalid (valid="false"), we focus the
 *   first invalid one. This ensures the user is brought back to fix the error.
 * - Otherwise, we focus according to the provided currentIndex (absolute cursor
 *   index among Sample ID inputs), clamped to the last available input.
 * - After focusing, place the caret at the end for a better scanning/editing UX.
 * - If focus is stolen by MUI during render, schedule another focus in rAF.
 *
 * Params:
 * - currentIndex: the intended absolute index among Sample ID inputs to focus
 *      when there are no invalid inputs to correct.
 */
export function manageSampleFocus(currentIndex: number) {
  // Gather Sample ID input nodes. We rely on the injected 'valid' attribute to
  // identify them, so we don't accidentally include non-SampleID fields.
  const inputs = Array.prototype.slice
    .call(
      document.querySelectorAll(
        'input.MuiInputBase-input.MuiOutlinedInput-input',
      ),
    )
    .filter((item) => item.attributes?.valid)

  if (inputs.length > 0) {
    // Prefer focusing the first invalid Sample ID input if any exist
    const firstInvalidIdx = inputs.findIndex(
      (el: any) => el.getAttribute && el.getAttribute('valid') === 'false',
    )

    // Otherwise, focus according to the cursor (clamped to last input)
    const idx =
      firstInvalidIdx !== -1 ? firstInvalidIdx : (
        Math.min(currentIndex, inputs.length - 1)
      )

    const el: HTMLInputElement = inputs[idx]

    // If the element exists but did not retain focus after a re-render, re-apply focus
    if (document.activeElement !== el) {
      // Immediate focus attempt
      el.focus()
      try {
        // Also place cursor at end for better scan/edit UX
        const len = el.value?.length ?? 0
        el.setSelectionRange(len, len)
      } catch {}

      // In some cases MUI may steal focus during render; schedule a microtask to enforce
      if (document.activeElement !== el) {
        requestAnimationFrame(() => {
          el.focus()
          try {
            const len = el.value?.length ?? 0
            el.setSelectionRange(len, len)
          } catch {}
        })
      }
    }
  }
}
